<template lang="html">
  <!-- callbacks drag events proxied  -->
  <section
    class="trend-chart"
    :id="id"
    :draggable="draggable"
    @dragstart.stop="onComponentStartDrag($event)"
    @dragend.stop="onComponentEndDrag($event)"
    @dragover.prevent.stop
    @dragenter.stop="onComponentDragEnter($event)"
  >
    <div class="m-header">
      <div class="title">{{ messages.DISP_EL_TREND.Text_vod__c }}</div>
      <settings-element
        @on-settings-edit-click="onSettingsClick"
        @on-settings-delete-click="onDeleteClick"
      />
    </div>

    <div class="card-description normal-wrap">
      {{ description }}
    </div>

    <div class="meta-data" v-if="dataSource">
      <div class="meta-row">
        <div class="label">{{ messages.DATA_SOURCE.Text_vod__c }}</div>
      </div>
      <div class="value" :title="dataSource.name">{{ dataSource.name }}</div>

      <div class="meta-row">
        <div class="label">{{ messages.TREND_DATE_LABEL.Text_vod__c }}</div>
        <div class="value" :title="dateExpLabel">{{ dateExpLabel }}</div>
      </div>

      <div class="meta-row">
        <div class="label">{{ messages.TREND_VALUE_LABEL.Text_vod__c }}</div>
        <div class="value" :title="valueFormatExpLabel">
          {{ valueFormatExpLabel }}
        </div>
      </div>

      <div class="meta-row">
        <div class="label">{{ messages.TREND_SERIES_LABEL.Text_vod__c }}</div>
        <div class="value" :title="seriesFormatExpLabel">
          {{ seriesFormatExpLabel }}
        </div>
      </div>

      <div class="meta-row">
        <div class="label">
          {{ messages.TREND_DATE_UNIT_LABEL.Text_vod__c }}
        </div>
        <div class="value" :title="timeUnit.name">{{ timeUnit.name }}</div>
      </div>
    </div>

    <simple-modal
      :show="displaySimpleModal"
      :message="messages.ARE_YOU_SURE.Text_vod__c"
      :cancel-text="messages.CANCEL.Text_vod__c"
      :ok-text="messages.OK.Text_vod__c"
      @on-simple-modal-cancel="onCancelDeleteElement"
      @on-simple-modal-ok="onConfirmDeleteElement"
    />
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import utils from "@/utils";
  import { MUTATION_NAME } from "@/static/global-variables";
  import dateUtil from "@/utils/date-util";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import SettingsElement from "@/views/drag-drop-pages/components/SettingsElement";

  export default  {
    name: "trend-chart",
    components: {
      SimpleModal,
      SettingsElement
    },
    props: {
      id: {
        type: String
      },
      // props specific to draggable component
      allowDropzones: {
        type: Boolean,
        default: true
      },
      dropEffect: {
        type: String,
        default: dragAndDrop.DROP_EFFECT.MOVE
      },
      draggable: {
        type: Boolean,
        default: true
      },
      description: {
        type: String
      },
      // props specific to this component below
      data: {
        type: Object
      }
    },
    mounted () {
      this.setData(this.data);
    },
    data () {
      return {
        displaySimpleModal: false,
        dataSource: null,
        date: null,
        value: null,
        series: null,
        timeUnit: null,
        unitOptions: dateUtil.getUnitOptions(),
        staticType: expressionBuilder.EXPRESSION_TYPE.STATIC
      }
    },
    computed: {
      dateExpLabel() {
        return utils.findExpressionLabel(this.date);
      },
      valueFormatExpLabel() {
        return utils.findExpressionLabel(this.value);
      },
      seriesFormatExpLabel() {
        return utils.findExpressionLabel(this.series);
      },
      messages() {
        return store.state.messages;
      },
      appId() {
        return dragAndDrop.getAppId();
      },
      appContent() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      },
      deleteModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_DELETE_DRAGGABLE, data);
        },
        get() {
          return store.getters.deleteDraggableItem;
        }
      },
      editModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_EDIT_DRAGGABLE, data);
        },
        get() {
          return store.getters.editDraggableItem;
        }
      }
    },
    watch: {
      data: {
        deep: true,
        handler() {
          this.setData(this.data);
        }
      }
    },
    methods: {
      setData(data) {
        if(data) {
          this.dataSource = data.dataSource;
          this.date = data.date;
          this.value = data.value;
          this.series = data.series;
          this.timeUnit = data.timeUnit;
        }
      },
      onCancelDeleteElement() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteElement() {
        this.displaySimpleModal = false;
        const obj = {
          id: this.id
        };
        this.deleteModal = {detail: obj};
      },
      onDeleteClick() {
        this.displaySimpleModal = true;
      },

      onSettingsClick() {
        const obj = {
          id: this.id,
          modal: "AddTrendChartModal",
          dropEffect: dragAndDrop.DROP_EFFECT.NONE,
          props: {
            description: this.description,
            dataSource: this.dataSource,
            date: this.date,
            value: this.value,
            series: this.series,
            timeUnit: this.timeUnit
          }
        };
        this.editModal = {detail: obj};
      },
      getUtilityIcon(iconName) {
        return iconsUri.getUtilityUrl(iconName);
      },
      /**
       * @function getMetadata
       * @returns {Object} Metadata in JSON format
      */
     getMetadata() {
        // meta must contain meta.element object with at least meta.element.name, do not remove
        return {
          element: {
            name: this.$options.name // this field is required for all components
          },
          // add elements required for this component such as type, datasource and others
          type: "Trend Chart",
          description: this.description,
          dataSource: this.dataSource.id,
          date: this.date,
          value: this.value,
          series: this.series,
          timeUnit: this.timeUnit.value
        };
      },
      /**
       * @function deserialize
       * @param {Object} serializedJson - Metadata to create content of this component
      */
      deserialize(serializedJson) {
        const dataSource = this.appContent.dataElements[serializedJson.dataSource];
        this.description = serializedJson.description;
        this.setData({
          dataSource: dataSource,
          date: serializedJson.date,
          value: serializedJson.value,
          series: serializedJson.series,
          timeUnit: this.unitOptions.find(e => e.value === serializedJson.timeUnit)
        });
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  .trend-chart {
    flex: 1;
    min-width: 150px;
    width: 100%;
    position: relative; /* component must be position relative */
    @include mixin-box;
    padding: 12px;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--DEFAULT_TEXT_BLACK);
  }
  .m-shrink {
    @include mixin-shrink-layout;
  }
  .m-header {
    display: grid;
    box-sizing: border-box;
    grid-template-columns: auto 3rem;
    .title {
      font-weight: bold;
      font-size: 0.9rem;
    }
  }
  .meta-data {
    display: flex;
    flex-direction: column;
    margin-bottom: 5px;
    .meta-row {
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      margin-bottom: 0.5rem;

      .label,
      .value {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 1; /* number of lines to show */
        line-clamp: 1;
        -webkit-box-orient: vertical;
      }
      .value {
        font-weight: bold;
        font-size: 1rem;
      }
    }
  }
</style>
